import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
  Box,
  Button,
  ButtonProps,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { FormikState, FormikHelpers, FormikHandlers } from 'formik';
import React, { ChangeEvent, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { DocumentUploadState } from '../../../../domain/document-collection';
import { RequiredText } from '../../../../general/components/required-text';

type Props = {
  formik: FormikHandlers &
    FormikHelpers<{ documents: DocumentUploadState[] }> &
    FormikState<{ documents: DocumentUploadState[] }>;
  documentIndex: number;
  pdfOnly?: boolean;
  uploadingError?: boolean;
};

const getFirstItem = (items: FileList | null): File | null => {
  return (items && Array.from(items)[0]) ?? null;
};

const PDF_MIME_TYPE = 'application/pdf';

const DocumentUploadCard: React.FC<Props> = ({ formik, documentIndex, pdfOnly, uploadingError = false }: Props) => {
  const document = formik.values.documents[documentIndex];
  const { entityName, documentName, file, uploading, progress, progressComputable } = document;
  const fileFieldName = `documents.${documentIndex}.file`;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        void formik.setFieldValue(fileFieldName, acceptedFiles[0]);
      }
    },
    [formik, fileFieldName],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: pdfOnly ? PDF_MIME_TYPE : '',
  });

  const changeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      void formik.setFieldValue(fileFieldName, getFirstItem(event.target.files));
    },
    [formik, fileFieldName],
  );

  const removeChangeHandler: NonNullable<ButtonProps<'div'>['onClick']> = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      void formik.setFieldValue(fileFieldName, null);
    },
    [formik, fileFieldName],
  );

  const fileFieldMeta = formik.getFieldMeta(fileFieldName);
  const error = fileFieldMeta.touched && fileFieldMeta.error;
  const pdfOnlyText = pdfOnly ? (
    <Typography variant="caption">
      <RequiredText>PDF only</RequiredText>
    </Typography>
  ) : null;
  return (
    <Card variant="outlined">
      <CardActionArea {...getRootProps()}>
        <input aria-label={`Upload ${documentName} for ${entityName}`} {...getInputProps()} onChange={changeHandler} />
        <CardContent>
          <Box display="flex" alignItems="center">
            <Box mr={3}>
              <Typography variant="body2" color="primary">
                {entityName}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {documentName}
              </Typography>
            </Box>
            <Typography variant="inherit" color="textSecondary" sx={{ ml: 'auto' }}>
              <CloudUploadOutlinedIcon />
            </Typography>
          </Box>
          {pdfOnlyText}
          {file && (
            <Box mt={2} display="flex" alignItems="center">
              {(uploading || progress === 100) && !uploadingError ? (
                <CircularProgress
                  size={16}
                  variant={progressComputable ? 'determinate' : 'indeterminate'}
                  value={progress}
                  color="success"
                  sx={{ mr: 1 }}
                />
              ) : null}
              <Typography variant="inherit" color="textSecondary" data-private>
                {file.name}
              </Typography>

              <Button
                variant="text"
                size="large"
                color={'danger' as ButtonProps['color']}
                onClick={removeChangeHandler}
                component="div"
                sx={{ ml: 'auto' }}
              >
                remove
              </Button>
            </Box>
          )}
          {error && (
            <Box mt={2}>
              <Typography variant="inherit" color="error">
                {error}
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export { DocumentUploadCard };
